'use client';

import { useRef, useState, useEffect, type ReactNode } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  type Variants,
} from 'framer-motion';
import { cn } from '@/lib/utils';
import { useSafeReducedMotion } from '@/hooks/useSafeReducedMotion';

const defaultEase = [0.22, 1, 0.36, 1] as const;

/* Every primitive below branches its rendered element/animation on reduced
   motion. `useSafeReducedMotion` returns `false` on the server and the first
   client render (so markup matches), then the real preference after mount.
   Without this, branching on the raw `useReducedMotion()` value causes a
   hydration mismatch that can discard the whole tree. */

/* Framer Motion drives transforms through rAF/WAAPI, so the global
   `transition-duration: 0.01ms` reduced-motion rule in globals.css never
   reaches it. Every primitive below therefore checks reduced motion
    (via the SSR-safe `useSafeReducedMotion`) and degrades to a plain,
    instantly-visible element. */

/* ── FadeIn: reveals children with a subtle upward slide ── */
export function FadeIn({
  children,
  className,
  delay = 0,
  duration = 0.55,
  y = 28,
  once = true,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  y?: number;
  once?: boolean;
}) {
  const reduce = useSafeReducedMotion();

  if (reduce) return <div className={className}>{children}</div>;

  /* Uses Framer's own `whileInView` rather than a hand-rolled
     `useInView` + `animate` pair.

     Why it changed: with the manual version, eight blocks on /product — most
     of that page's content — sat at inline `opacity: 0` permanently. Through a
     full scroll pass, through synthetic scroll events, through a resize, with
     prefers-reduced-motion off. `useInView` simply never reported them, and
     because the visible state was gated on it, the content was gone for good.
     Content that only appears if an animation succeeds is a content bug
     wearing an animation costume, and it fails silently.

     `whileInView` owns the observer lifecycle itself, which removes the class
     of bug rather than papering over it.

     `amount: 0` (any pixel counts) instead of the previous 0.2: a fifth of a
     tall two-column block is a lot of pixels, and those were exactly the ones
     that never qualified. The negative bottom margin starts the reveal just
     before the block reaches the fold. */
  /* NOTE: `opacity` is deliberately NOT animated from 0.

     It used to be, and it cost eight blocks on /product — most of that page's
     content — which sat at inline `opacity: 0` permanently. Verified: a block
     588px tall, centred in a 900px viewport, fully on screen, still at
     opacity 0 after 2.5s, no console errors, reduced-motion off. The observer
     never fired for it and the content was simply gone.

     Rather than keep guessing at why the observer is unreliable, the reveal no
     longer has the power to hide anything. It animates transform only. If the
     observer fails now, the block sits 28px from its resting position — which
     nobody will ever notice — instead of being invisible. A decorative effect
     must not be able to delete content. */
  return (
    <motion.div
      initial={{ y }}
      whileInView={{ y: 0 }}
      viewport={{ once, amount: 0, margin: '0px 0px -8% 0px' }}
      transition={{ duration, delay, ease: defaultEase }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── StaggerChildren: container that staggers child reveals ── */
const staggerContainer: Variants = {
  hidden: {},
  visible: (staggerDelay: number = 0.06) => ({
    transition: { staggerChildren: staggerDelay },
  }),
};

export function StaggerChildren({
  children,
  className,
  staggerDelay = 0.06,
  once = true,
}: {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
  once?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once, amount: 0, margin: '0px 0px -8% 0px' });
  const reduce = useSafeReducedMotion();

  /* Children use the shared `staggerItem` variants, so under reduced motion
     the container must still drive them to `visible` , otherwise they stay at
     opacity 0 forever. Keep the variant plumbing, drop the stagger. */
  return (
    <motion.div
      ref={ref}
      variants={staggerContainer}
      initial={reduce ? 'visible' : 'hidden'}
      animate={reduce || inView ? 'visible' : 'hidden'}
      custom={reduce ? 0 : staggerDelay}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* Same rule as FadeIn: transform only, never opacity — a stalled stagger must
   not be able to blank a card. */
export const staggerItem: Variants = {
  hidden: { y: 22 },
  visible: {
    y: 0,
    transition: { duration: 0.5, ease: defaultEase },
  },
};

/* ── ParallaxSection: translates Y based on scroll position ── */
export function ParallaxSection({
  children,
  className,
  id,
  offset = 80,
  speed = 0.15,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  offset?: number;
  speed?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useSafeReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [offset * speed, -offset * speed]);

  if (reduce) {
    return (
      <div id={id} className={className}>
        {children}
      </div>
    );
  }

  return (
    <div ref={ref} id={id} className={cn('overflow-hidden', className)}>
      <motion.div style={{ y }}>{children}</motion.div>
    </div>
  );
}

/* ── ScaleIn: scales up from 0.92 with fade ── */
export function ScaleIn({
  children,
  className,
  delay = 0,
  once = true,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  once?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /* `amount: 0` (any pixel visible) rather than 0.2, plus a negative bottom
     margin so the reveal starts just before the block reaches the fold.
     0.2 demanded a fifth of the element on screen, which is a bad bet for the
     tall two-column blocks on /product — several never qualified and stayed
     invisible for good. */
  const inView = useInView(ref, { once, amount: 0, margin: '0px 0px -8% 0px' });
  const reduce = useSafeReducedMotion();

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      ref={ref}
      initial={{ scale: 0.92 }}
      animate={inView ? { scale: 1 } : { scale: 0.92 }}
      transition={{ duration: 0.6, delay, ease: defaultEase }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── SlideIn: slides in from left or right ── */
export function SlideIn({
  children,
  className,
  from = 'left',
  delay = 0,
  once = true,
}: {
  children: ReactNode;
  className?: string;
  from?: 'left' | 'right';
  delay?: number;
  once?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /* `amount: 0` (any pixel visible) rather than 0.2, plus a negative bottom
     margin so the reveal starts just before the block reaches the fold.
     0.2 demanded a fifth of the element on screen, which is a bad bet for the
     tall two-column blocks on /product — several never qualified and stayed
     invisible for good. */
  const inView = useInView(ref, { once, amount: 0, margin: '0px 0px -8% 0px' });
  const reduce = useSafeReducedMotion();
  const x = from === 'left' ? -60 : 60;

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      ref={ref}
      initial={{ x }}
      animate={inView ? { x: 0 } : { x }}
      transition={{ duration: 0.65, delay, ease: defaultEase }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── RevealLine: draws a hairline in from the left as it enters view.
   Used to separate sections without printing a divider on every row. ── */
export function RevealLine({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduce = useSafeReducedMotion();

  return (
    <div ref={ref} className={cn('h-px w-full overflow-hidden', className)}>
      <motion.div
        className="h-full w-full origin-left bg-[currentColor] opacity-[0.14]"
        initial={reduce ? false : { scaleX: 0 }}
        animate={reduce || inView ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 0.9, ease: defaultEase }}
      />
    </div>
  );
}

/* ── Parallax ──────────────────────────────────────────────────────────────
   GPU-only (transform) scroll parallax. The element drifts opposite to the
   scroll direction as it passes through the viewport, giving depth without
   layout thrash. Fully disabled under prefers-reduced-motion. */
export function Parallax({
  children,
  className,
  speed = 0.5,
  offset = 90,
}: {
  children: ReactNode;
  className?: string;
  speed?: number;
  offset?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useSafeReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const distance = offset * speed;
  const y = useTransform(scrollYProgress, [0, 1], [distance, -distance]);

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <div ref={ref} className={className}>
      <motion.div style={{ y, willChange: 'transform' }}>{children}</motion.div>
    </div>
  );
}

/* ── BlurFadeIn ─────────────────────────────────────────────────────────────
   Premium entrance: fades up while resolving a soft blur. Used for lead
   graphics and section titles. Disabled under prefers-reduced-motion. */
export function BlurFadeIn({
  children,
  className,
  delay = 0,
  duration = 0.7,
  y = 22,
  blur = 7,
  once = true,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  y?: number;
  blur?: number;
  once?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useSafeReducedMotion();
  const inView = useInView(ref, { once, amount: 0.25 });

  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      ref={ref}
      initial={{ y, filter: `blur(${blur}px)` }}
      animate={inView ? { y: 0, filter: 'blur(0px)' } : { y, filter: `blur(${blur}px)` }}
      transition={{ duration, delay, ease: defaultEase }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
